/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useContext, useRef } from 'react';
import { ListboxSelect, ListboxMultiselect } from '@evertrue/et-components';
import { TasksContext } from 'apps/tasks/hooks/tasks-context';
import { trackTaskAction } from 'apps/tasks/utils/utils';
import useFetchAllUsers from 'apps/search/hooks/use-fetch-all-users';
import useTasks from 'apps/tasks/hooks/use-tasks';
import useCadences from 'apps/cadences-v2/hooks/use-cadences';
import { fetchTaskCadenceIds } from 'apps/tasks/utils/fetch-helpers';
import { useIdentity } from 'base/identity-resolver';
import { useFeatureFlag } from 'entities/helpers/use-feature-flag';
import Icon from 'components/elements/icon';
import DateFilterPopover from 'apps/cadences-v2/components/date-filter-popover';
import FilterMultiselect from 'components/forms/filter-multiselect';
import Api from 'entities/helpers/api';
import Decorator from '@evertrue/client-decorator';
import { getQueryTemplate } from 'apps/layout/global-search/config/global-search-config';

const taskCompletionOptions = [
  { label: 'Incomplete Tasks', value: ['IN_PROGRESS', 'PAUSED'] },
  { label: 'Completed Tasks', value: ['COMPLETED'] },
  { label: 'All Tasks', value: ['IN_PROGRESS', 'COMPLETED', 'PAUSED'] },
];

const allTaskTypeOptions = [
  { label: 'Cadence 1.0', value: 'cadence_1' },
  { label: 'Cadence 2.0', value: 'cadence_2' },
  { label: 'Non-Cadence Task', value: 'non_cadence' },
];

const groupByOptions = [
  { label: 'None', value: 'none' },
  { label: 'Cadence Step', value: 'cadence_step' },
];

const DEFAULT_DATE_FILTER = { operator: 'all', date1: null, date2: null };

const TaskQuickFilters = ({ userType }) => {
  const {
    taskFilters,
    updateTaskFilters,
    filters,
    setFilters,
    filtersDefaultValue,
    referrer,
    groupBy,
    setGroupBy,
    constituentSearch,
    setConstituentSearch,
    completedDateRange,
    setCompletedDateRange,
    creationDateRange,
    setCreationDateRange,
    dueDateRange,
    setDueDateRange,
    setTaskTypeClientFilter,
    setCadenceNameFilter,
    userId,
    groupSortOrder,
    setGroupSortOrder,
  } = useContext(TasksContext);

  const { flagEnabled: cadenceV2Enabled } = useFeatureFlag('cadenceV2Internal');
  const { flagEnabled: thankviewBetaEnabled } = useFeatureFlag('thankviewBeta');
  const taskTypeOptions = cadenceV2Enabled
    ? allTaskTypeOptions
    : allTaskTypeOptions
        .filter((o) => o.value !== 'cadence_2')
        .map((o) => (o.value === 'cadence_1' ? { ...o, label: 'Cadence Task' } : o));

  // One-time lightweight fetch of ALL the user's task cadence/journey IDs (up to 10k)
  // so filter dropdowns show exactly the cadences that have tasks.
  const [taskCadence2Ids, setTaskCadence2Ids] = useState(new Set());
  const [taskJourney1Map, setTaskJourney1Map] = useState(new Map());
  useEffect(() => {
    if (!userId) return;
    if (!cadenceV2Enabled) return;
    fetchTaskCadenceIds(userId).then(({ cadence2Ids, journey1Map }) => {
      setTaskCadence2Ids(cadence2Ids);
      setTaskJourney1Map(journey1Map);
    });
  }, [userId, cadenceV2Enabled]);

  // 1.0 options come directly from the task data (journey_name is on the task metadata)
  const cadence1Options = [...taskJourney1Map.entries()].map(([id, name]) => ({ label: name, value: id }));

  // 2.0 options: cross-reference the IDs from tasks with the full cadences list for accurate names
  const { cadences: allCadences } = useCadences({ enabled: cadenceV2Enabled });
  const cadence2Options = (allCadences || [])
    .filter(
      (c) =>
        (c.cadenceType === 'cadence' || c.cadenceType === 'prospect_plan') &&
        c.status !== 'archived' &&
        !c.isTemplate &&
        taskCadence2Ids.has(String(c.id))
    )
    .map((c) => ({
      label: c.name,
      value: String(c.id),
    }));
  const [selectedCadence2Ids, setSelectedCadence2Ids] = useState([]);
  const [selectedCadence1Ids, setSelectedCadence1Ids] = useState([]);

  // When either cadence name filter changes, build a combined filter
  useEffect(() => {
    const has1 = selectedCadence1Ids.length > 0;
    const has2 = selectedCadence2Ids.length > 0;
    if (!has1 && !has2) {
      setCadenceNameFilter(null);
      return;
    }
    const id1Set = has1 ? new Set(selectedCadence1Ids.map((o) => o.value)) : null;
    const id2Set = has2 ? new Set(selectedCadence2Ids.map((o) => o.value)) : null;
    setCadenceNameFilter(() => (t) => {
      if (id1Set) {
        const journeyId = t.metadata?.journey_id;
        if (journeyId && id1Set.has(String(journeyId))) return true;
      }
      if (id2Set) {
        if (t.cadence_id && id2Set.has(String(t.cadence_id))) return true;
      }
      return false;
    });
  }, [selectedCadence1Ids, selectedCadence2Ids, setCadenceNameFilter]);

  // Additional filters available via "+ Add Filter"
  const additionalFilterOptions = [];
  if (referrer === 'contact') {
    additionalFilterOptions.push(
      { label: 'Created By', value: 'creator_user_ids', filterName: 'taskCreators' },
      { label: 'Assignee', value: 'owner_user_ids', filterName: 'assignees' }
    );
  } else if (cadenceV2Enabled) {
    additionalFilterOptions.push(
      { label: '1.0 Cadence Name', value: 'cadence_1_name', filterName: 'cadence1Name' },
      { label: '2.0 Cadence Name', value: 'cadence_2_name', filterName: 'cadence2Name' },
      { label: 'Constituent Name', value: 'target_contact_ids', filterName: 'constituents' },
      { label: 'Creation Date', value: 'creation_date', filterName: 'creationDate' },
      { label: 'Due Date', value: 'due_date_range', filterName: 'dueDateRange' },
      { label: 'Completed Date', value: 'completed_date_range', filterName: 'completedDateRange' }
    );
  } else {
    additionalFilterOptions.push(
      { label: 'Action Type', value: 'action_types', filterName: 'actionTypes' },
      { label: 'Task Type', value: 'task_types', filterName: 'taskTypes' },
      ...(userType === 'OWNER' || referrer === 'contact'
        ? [{ label: 'Created By', value: 'creator_user_ids', filterName: 'taskCreators' }]
        : []),
      ...(userType === 'CREATOR_BUT_NOT_OWNER'
        ? [{ label: 'Assignee', value: 'owner_user_ids', filterName: 'assignees' }]
        : []),
      { label: 'Constituent Name', value: 'target_contact_ids', filterName: 'constituents' }
    );
  }

  const { user: currentUser } = useIdentity();
  const { users, fetchUsers } = useFetchAllUsers();
  const [taskActionTypeOptions, setTaskActionTypeOptions] = useState([]);
  const { fetchFormattedActionTypes } = useTasks();
  const [selectedQuickFilters, setSelectedQuickFilters] = useState([]);
  const addFilterRef = useRef(null);
  const [contactOptions, setContactOptions] = useState([]);
  const contactSearchTimeout = useRef(null);

  const parseContactOptions = (rawItems) =>
    rawItems.map(Decorator.Contacts.parse).map((c) => {
      const name =
        c.name ||
        c.properties?.name_full?.value ||
        [c.properties?.name_first?.value, c.properties?.name_last?.value].filter(Boolean).join(' ') ||
        'Unknown';
      return { label: name, value: c.id };
    });

  // Load initial contacts so the component sees >10 options and enables its search box
  const contactsInitialized = useRef(false);
  useEffect(() => {
    if (contactsInitialized.current) return;
    contactsInitialized.current = true;
    Api.CONTACTS.SEARCH.post({
      data: JSON.stringify(getQueryTemplate('a', 'name')),
      params: { limit: 20 },
    })
      .then((response) => setContactOptions(parseContactOptions(response?.items || response || [])))
      .catch(() => {});
  }, []);

  const searchContacts = (query) => {
    clearTimeout(contactSearchTimeout.current);
    contactSearchTimeout.current = setTimeout(async () => {
      const trimmed = (query || '').trim();
      if (!trimmed) {
        // Reset to initial set rather than empty
        Api.CONTACTS.SEARCH.post({
          data: JSON.stringify(getQueryTemplate('a', 'name')),
          params: { limit: 20 },
        })
          .then((response) => setContactOptions(parseContactOptions(response?.items || response || [])))
          .catch(() => {});
        return;
      }
      try {
        const response = await Api.CONTACTS.SEARCH.post({
          data: JSON.stringify(getQueryTemplate(trimmed, 'name')),
          params: { limit: 20 },
        });
        setContactOptions(parseContactOptions(response?.items || response || []));
      } catch {
        setContactOptions([]);
      }
    }, 300);
  };

  useEffect(() => {
    const fetchData = async () => {
      const actionTypes = await fetchFormattedActionTypes();
      setTaskActionTypeOptions(actionTypes);
    };
    fetchData();
  }, []);

  // RECORD_VIDEO rides along with the ThankView beta flag; the backend returns it to every org.
  const visibleTaskActionTypeOptions = Array.isArray(taskActionTypeOptions)
    ? taskActionTypeOptions.filter(({ value }) => thankviewBetaEnabled || value !== 'RECORD_VIDEO')
    : taskActionTypeOptions;

  const usersOptions = users.map((user) => {
    return { label: user.id === currentUser.id ? user.name + ' (You)' : user.name, value: user.id };
  });

  let searchQueryTimeout = null;

  // Pre-select filters from URL params (e.g., ?taskTypes=cadence_1,cadence_2&status=incomplete)
  const urlParamsApplied = useRef(false);
  useEffect(() => {
    if (urlParamsApplied.current) return;
    const params = new URLSearchParams(window.location.search);
    let applied = false;

    // Task types: ?taskTypes=cadence_1,cadence_2 or ?taskTypes=non_cadence
    const taskTypesParam = params.get('taskTypes');
    if (taskTypesParam) {
      const values = taskTypesParam.split(',').filter(Boolean);
      const matched = taskTypeOptions.filter((opt) => values.includes(opt.value));
      if (matched.length > 0) {
        handleTaskTypeChange(matched);
        applied = true;
      }
    }

    // Status: ?status=incomplete|completed|all
    const statusParam = params.get('status');
    if (statusParam) {
      const statusMap = {
        incomplete: taskCompletionOptions[0],
        completed: taskCompletionOptions[1],
        all: taskCompletionOptions[2],
      };
      const match = statusMap[statusParam];
      if (match) {
        setFilters((prev) => ({ ...prev, taskCompletion: match }));
        updateTaskFilters((prev) => ({ ...prev, statuses: match.value }));
        applied = true;
      }
    }

    // Completed date range: ?completedDateStart=2026-03-01&completedDateEnd=2026-03-31 (month bar)
    // or single date: ?completedDate=2026-03-09 (day bar)
    const completedDateStart = params.get('completedDateStart');
    const completedDateEnd = params.get('completedDateEnd');
    const completedDateParam = params.get('completedDate');
    if (completedDateStart && completedDateEnd) {
      setCompletedDateRange({ operator: 'is_between', date1: completedDateStart, date2: completedDateEnd });
      applied = true;
    } else if (completedDateParam) {
      setCompletedDateRange({ operator: 'is_on', date1: completedDateParam, date2: null });
      applied = true;
    }

    // Due date: ?dueDateBefore=2026-03-13 (is_before)
    // or range: ?dueDateStart=2026-03-01&dueDateEnd=2026-03-31
    // or single date: ?dueDate=2026-03-09
    const dueDateBefore = params.get('dueDateBefore');
    const dueDateStart = params.get('dueDateStart');
    const dueDateEnd = params.get('dueDateEnd');
    const dueDateParam = params.get('dueDate');
    if (dueDateBefore) {
      setDueDateRange({ operator: 'is_before', date1: dueDateBefore, date2: null });
      applied = true;
    } else if (dueDateStart && dueDateEnd) {
      setDueDateRange({ operator: 'is_between', date1: dueDateStart, date2: dueDateEnd });
      applied = true;
    } else if (dueDateParam) {
      setDueDateRange({ operator: 'is_on', date1: dueDateParam, date2: null });
      applied = true;
    }

    if (applied) urlParamsApplied.current = true;
  }, []);

  // Auto-surface optional date filters when they have active values
  useEffect(() => {
    const dateFiltersToShow = [
      { state: completedDateRange, value: 'completed_date_range' },
      { state: dueDateRange, value: 'due_date_range' },
      { state: creationDateRange, value: 'creation_date' },
    ];
    const toAdd = [];
    for (const { state, value } of dateFiltersToShow) {
      if (state && state.operator !== 'all') {
        const opt = additionalFilterOptions.find((o) => o.value === value);
        if (opt) toAdd.push(opt);
      }
    }
    if (toAdd.length > 0) {
      setSelectedQuickFilters((prev) => {
        const existingValues = new Set(prev.map((f) => f.value));
        const newFilters = toAdd.filter((f) => !existingValues.has(f.value));
        return newFilters.length > 0 ? [...prev, ...newFilters] : prev;
      });
    }
  }, [completedDateRange, dueDateRange, creationDateRange]);

  useEffect(() => {
    setSelectedQuickFilters([]);
  }, [userType]);

  const resetQuickFilters = (filter) => {
    if (filter) {
      if (filter === 'isCadenceTask' || filter === 'taskTypes') {
        setFilters({ ...filters, [filter]: filter === 'taskTypes' ? [] : undefined });
      } else {
        setFilters({ ...filters, [filter]: [] });
      }
    } else {
      setFilters(filtersDefaultValue);
      setSelectedQuickFilters([]);
    }
  };

  const handleSelectedFiltersChange = (vals) => {
    const unselectedOptions = selectedQuickFilters.filter((option) => !vals.includes(option));
    if (unselectedOptions.length > 0) {
      const unselectedKey = unselectedOptions[0].value;
      if (unselectedKey in taskFilters) {
        const { [unselectedKey]: _, ...remainingFilters } = taskFilters;
        updateTaskFilters(remainingFilters);
      }
      // Reset client-side filter state for removed filters
      if (unselectedKey === 'cadence_2_name') {
        setSelectedCadence2Ids([]);
      } else if (unselectedKey === 'cadence_1_name') {
        setSelectedCadence1Ids([]);
      } else if (unselectedKey === 'completed_date_range') {
        setCompletedDateRange(DEFAULT_DATE_FILTER);
      } else if (unselectedKey === 'creation_date') {
        setCreationDateRange(DEFAULT_DATE_FILTER);
      } else if (unselectedKey === 'due_date_range') {
        setDueDateRange(DEFAULT_DATE_FILTER);
      } else if (unselectedKey === 'target_contact_ids') {
        setConstituentSearch([]);
      } else if (unselectedKey === 'action_types') {
        clearCoreFilter('actionTypes');
      } else if (unselectedKey === 'task_types') {
        clearCoreFilter('taskTypes');
      } else if (unselectedKey === 'owner_user_ids') {
        clearCoreFilter('assignees');
      } else if (unselectedKey === 'creator_user_ids') {
        clearCoreFilter('taskCreators');
      }
      resetQuickFilters(unselectedOptions[0].filterName);
    }
    setSelectedQuickFilters(vals);
  };

  const handleFilterValueChange = (name, filter, values) => {
    setFilters({ ...filters, [name]: values });
    if (name === 'isCadenceTask') {
      updateTaskFilters({ ...taskFilters, cadence_task: values.value });
    } else {
      updateTaskFilters({
        ...taskFilters,
        [filter]: values.length === 0 ? undefined : values.map((item) => item.value),
      });
    }
  };

  /**
   * Handle Task Type multi-select changes.
   * Backend only supports cadence_task Boolean (true = cadence 1.0 journey tasks).
   * Cadence 2.0 tasks have task_source='cadence_v2' — no backend filter exists,
   * so we apply a client-side filter via setTaskTypeClientFilter.
   */
  const handleTaskTypeChange = (vals) => {
    setFilters({ ...filters, taskTypes: vals });
    const selected = vals.map((v) => v.value);
    const hasCadence1 = selected.includes('cadence_1');
    const hasCadence2 = selected.includes('cadence_2');
    const hasNonCadence = selected.includes('non_cadence');
    const { cadence_task: _, ...rest } = taskFilters;

    if (selected.length === 0) {
      // Nothing selected — show all, no filters
      updateTaskFilters(rest);
      setTaskTypeClientFilter(null);
    } else if (hasCadence1 && !hasCadence2 && !hasNonCadence) {
      // Only Cadence 1.0
      updateTaskFilters({ ...rest, cadence_task: true });
      setTaskTypeClientFilter(null);
    } else if (!hasCadence1 && !hasCadence2 && hasNonCadence) {
      // Only Non-Cadence — backend excludes cadence 1.0, client-side excludes cadence 2.0
      updateTaskFilters({ ...rest, cadence_task: false });
      setTaskTypeClientFilter(() => (t) => t.task_source !== 'cadence_v2');
    } else {
      // Any combo involving Cadence 2.0, or mixed selections:
      // Fetch all tasks (no cadence_task filter) and filter client-side.
      updateTaskFilters(rest);
      const isCadence1 = (t) => !!t.metadata?.journey_id;
      const isCadence2 = (t) => t.task_source === 'cadence_v2';
      setTaskTypeClientFilter(() => (t) => {
        if (hasCadence1 && isCadence1(t)) return true;
        if (hasCadence2 && isCadence2(t)) return true;
        if (hasNonCadence && !isCadence1(t) && !isCadence2(t)) return true;
        return false;
      });
    }
  };

  const defaultCompletion = filtersDefaultValue.taskCompletion;
  const isCompletionChanged =
    JSON.stringify(filters?.taskCompletion?.value) !== JSON.stringify(defaultCompletion?.value);

  const clearCoreFilter = (filterName) => {
    if (filterName === 'taskCompletion') {
      const def = defaultCompletion || taskCompletionOptions[0];
      setFilters({ ...filters, taskCompletion: def });
      updateTaskFilters({ ...taskFilters, statuses: def.value });
    } else if (filterName === 'actionTypes') {
      setFilters({ ...filters, actionTypes: [] });
      const { action_types: _, ...rest } = taskFilters;
      updateTaskFilters(rest);
    } else if (filterName === 'taskTypes') {
      setFilters({ ...filters, taskTypes: [] });
      const { cadence_task: _, ...rest } = taskFilters;
      updateTaskFilters(rest);
      setTaskTypeClientFilter(null);
    } else if (filterName === 'taskCreators') {
      setFilters({ ...filters, taskCreators: [] });
      const { creator_user_ids: _, ...rest } = taskFilters;
      updateTaskFilters(rest);
    } else if (filterName === 'assignees') {
      setFilters({ ...filters, assignees: [] });
      const { owner_user_ids: _, ...rest } = taskFilters;
      updateTaskFilters(rest);
    }
  };

  return (
    <div className="tasks-filters-bar" style={{ paddingTop: userType ? '15px' : '8px' }}>
      <div className="tasks-filters-bar--left">
        {/* Always-visible: Task Completion */}
        <div className="tasks-filters-bar--core-filter">
          <ListboxSelect
            size="medium"
            options={taskCompletionOptions}
            onChange={(e) => {
              trackTaskAction(e.label);
              setFilters({ ...filters, taskCompletion: e });
              updateTaskFilters({ ...taskFilters, statuses: e.value });
            }}
            value={taskCompletionOptions.find(
              (option) => JSON.stringify(option.value) === JSON.stringify(filters?.taskCompletion?.value)
            )}
            emptyValue="Task Completion"
            label="Task Completion"
          />
          {isCompletionChanged && (
            <button
              type="button"
              className="tasks-filters-bar--clear-filter"
              onClick={() => clearCoreFilter('taskCompletion')}
              title="Reset Task Completion"
            >
              <Icon icon="clear" size={1} />
            </button>
          )}
        </div>

        {/* Core filter: Action Type (always-visible with v2, via + Add Filter without) */}
        {cadenceV2Enabled && (
          <div className="tasks-filters-bar--core-filter">
            <FilterMultiselect
              emptyValue="Action Type"
              label="Action Type"
              options={visibleTaskActionTypeOptions}
              onChange={(vals) => handleFilterValueChange('actionTypes', 'action_types', vals)}
              values={filters.actionTypes}
            />
            {filters.actionTypes.length > 0 && (
              <button
                type="button"
                className="tasks-filters-bar--clear-filter"
                onClick={() => clearCoreFilter('actionTypes')}
                title="Clear Action Type"
              >
                <Icon icon="clear" size={1} />
              </button>
            )}
          </div>
        )}

        {/* Core filter: Task Type (always-visible with v2, via + Add Filter without) */}
        {cadenceV2Enabled && (
          <div className="tasks-filters-bar--core-filter">
            <FilterMultiselect
              emptyValue="Task Type"
              label="Task Type"
              options={taskTypeOptions}
              onChange={handleTaskTypeChange}
              values={filters.taskTypes}
            />
            {filters.taskTypes.length > 0 && (
              <button
                type="button"
                className="tasks-filters-bar--clear-filter"
                onClick={() => clearCoreFilter('taskTypes')}
                title="Clear Task Type"
              >
                <Icon icon="clear" size={1} />
              </button>
            )}
          </div>
        )}

        {/* Core filter: Created By / Assignee (always-visible with v2, via + Add Filter without) */}
        {cadenceV2Enabled && (userType === 'OWNER' || referrer === 'contact') && (
          <div className="tasks-filters-bar--core-filter">
            <FilterMultiselect
              emptyValue="Created By"
              label="Created By"
              searchable={true}
              options={usersOptions}
              onChange={(vals) => handleFilterValueChange('taskCreators', 'creator_user_ids', vals)}
              onSearch={(search) => {
                clearTimeout(searchQueryTimeout);
                searchQueryTimeout = setTimeout(() => {
                  fetchUsers(search, 0);
                }, 500);
              }}
              scrollFetch={(search) => {
                fetchUsers(search, users.length);
              }}
              values={filters.taskCreators}
              showInfoBar={true}
            />
            {filters.taskCreators.length > 0 && (
              <button
                type="button"
                className="tasks-filters-bar--clear-filter"
                onClick={() => clearCoreFilter('taskCreators')}
                title="Clear Created By"
              >
                <Icon icon="clear" size={1} />
              </button>
            )}
          </div>
        )}
        {cadenceV2Enabled && userType === 'CREATOR_BUT_NOT_OWNER' && !referrer && (
          <div className="tasks-filters-bar--core-filter">
            <FilterMultiselect
              emptyValue="Assignee"
              label="Assignee"
              searchable={true}
              options={usersOptions}
              onChange={(vals) => handleFilterValueChange('assignees', 'owner_user_ids', vals)}
              onSearch={(search) => {
                clearTimeout(searchQueryTimeout);
                searchQueryTimeout = setTimeout(() => {
                  fetchUsers(search, 0);
                }, 500);
              }}
              scrollFetch={(search) => {
                fetchUsers(search, users.length);
              }}
              values={filters.assignees}
              showInfoBar={true}
            />
            {filters.assignees.length > 0 && (
              <button
                type="button"
                className="tasks-filters-bar--clear-filter"
                onClick={() => clearCoreFilter('assignees')}
                title="Clear Assignee"
              >
                <Icon icon="clear" size={1} />
              </button>
            )}
          </div>
        )}

        {/* Dynamic filters added via "+ Add Filter" — each with X to remove */}
        {selectedQuickFilters.map((filter) => (
          <div key={filter.value} className="tasks-filters-bar--added-filter">
            {filter.label === 'Constituent Name' && !referrer && (
              <FilterMultiselect
                emptyValue="Constituent"
                label="Constituent"
                searchable={true}
                options={contactOptions}
                onSearch={searchContacts}
                onChange={setConstituentSearch}
                values={constituentSearch}
                showInfoBar={true}
              />
            )}
            {filter.label === '1.0 Cadence Name' && (
              <FilterMultiselect
                emptyValue="1.0 Cadence Name"
                label="1.0 Cadence Name"
                searchable={true}
                options={cadence1Options}
                onChange={setSelectedCadence1Ids}
                values={selectedCadence1Ids}
              />
            )}
            {filter.label === '2.0 Cadence Name' && (
              <FilterMultiselect
                emptyValue="2.0 Cadence Name"
                label="2.0 Cadence Name"
                searchable={true}
                options={cadence2Options}
                onChange={setSelectedCadence2Ids}
                values={selectedCadence2Ids}
              />
            )}
            {filter.label === 'Creation Date' && (
              <DateFilterPopover
                value={creationDateRange || DEFAULT_DATE_FILTER}
                onChange={setCreationDateRange}
                label="Creation Date"
              />
            )}
            {filter.label === 'Due Date' && (
              <DateFilterPopover
                value={dueDateRange || DEFAULT_DATE_FILTER}
                onChange={setDueDateRange}
                label="Due Date"
              />
            )}
            {filter.label === 'Completed Date' && (
              <DateFilterPopover
                value={completedDateRange || DEFAULT_DATE_FILTER}
                onChange={setCompletedDateRange}
                label="Completed Date"
              />
            )}
            {filter.label === 'Action Type' && (
              <FilterMultiselect
                emptyValue="Action Type"
                label="Action Type"
                options={visibleTaskActionTypeOptions}
                onChange={(vals) => handleFilterValueChange('actionTypes', 'action_types', vals)}
                values={filters.actionTypes}
              />
            )}
            {filter.label === 'Task Type' && (
              <FilterMultiselect
                emptyValue="Task Type"
                label="Task Type"
                options={taskTypeOptions}
                onChange={handleTaskTypeChange}
                values={filters.taskTypes}
              />
            )}
            {filter.label === 'Assignee' && (
              <FilterMultiselect
                emptyValue="Assignee"
                label="Assignee"
                searchable={true}
                options={usersOptions}
                onChange={(vals) => handleFilterValueChange('assignees', 'owner_user_ids', vals)}
                onSearch={(search) => {
                  clearTimeout(searchQueryTimeout);
                  searchQueryTimeout = setTimeout(() => {
                    fetchUsers(search, 0);
                  }, 500);
                }}
                scrollFetch={(search) => {
                  fetchUsers(search, users.length);
                }}
                values={filters.assignees}
                showInfoBar={true}
              />
            )}
            {filter.label === 'Created By' && (
              <FilterMultiselect
                emptyValue="Created By"
                label="Created By"
                searchable={true}
                options={usersOptions}
                onChange={(vals) => handleFilterValueChange('taskCreators', 'creator_user_ids', vals)}
                onSearch={(search) => {
                  clearTimeout(searchQueryTimeout);
                  searchQueryTimeout = setTimeout(() => {
                    fetchUsers(search, 0);
                  }, 500);
                }}
                scrollFetch={(search) => {
                  fetchUsers(search, users.length);
                }}
                values={filters.taskCreators}
                showInfoBar={true}
              />
            )}
            <button
              type="button"
              className="tasks-filters-bar--remove-filter"
              onClick={() => {
                const updated = selectedQuickFilters.filter((f) => f.value !== filter.value);
                handleSelectedFiltersChange(updated);
              }}
              title={`Remove ${filter.label}`}
            >
              <Icon icon="clear" size={1} />
            </button>
          </div>
        ))}

        {/* + Add Filter — always at the end of the filter row */}
        {additionalFilterOptions.length > 0 && (
          <div ref={addFilterRef}>
            <ListboxMultiselect
              buttonClassName={'add-filter-btn'}
              emptyValue="+ Add Filter"
              alwaysShowLabel={true}
              size="compact"
              options={additionalFilterOptions}
              onChange={(vals) => {
                handleSelectedFiltersChange(vals);
                // Close the dropdown after selecting a filter
                addFilterRef.current?.querySelector('.et-listbox--trigger')?.click();
              }}
              values={selectedQuickFilters}
              label="+ Add Filter"
            />
          </div>
        )}
      </div>

      {/* Group By + Sort Order dropdowns — right-aligned (cadence v2 only) */}
      {cadenceV2Enabled && (
        <div className="tasks-filters-bar--right">
          <ListboxSelect
            size="medium"
            options={groupByOptions}
            onChange={(option) => setGroupBy(option.value)}
            value={groupByOptions.find((o) => o.value === groupBy)}
            emptyValue="Group By"
            label="GROUP BY:"
            alwaysShowLabel={true}
          />
          {groupBy !== 'none' && (
            <ListboxSelect
              size="medium"
              options={[
                { label: 'Most tasks first', value: 'desc' },
                { label: 'Fewest tasks first', value: 'asc' },
              ]}
              onChange={(option) => setGroupSortOrder(option.value)}
              value={[
                { label: 'Most tasks first', value: 'desc' },
                { label: 'Fewest tasks first', value: 'asc' },
              ].find((o) => o.value === groupSortOrder)}
              emptyValue="Sort Groups"
              label="SORT:"
              alwaysShowLabel={true}
            />
          )}
        </div>
      )}
    </div>
  );
};

TaskQuickFilters.propTypes = {
  userType: PropTypes.string,
};

export default TaskQuickFilters;
